import { useEffect, useRef, useState } from 'react';
import { Box, Drawer, Tooltip } from '@material-ui/core';
import {
  PageLoader,
  SButton,
  SETVIImage,
  SGallery,
  SText
} from '@setvi/shared/components';
import { DetailsDialogProps } from 'componentsV2';
import { useAskAIStorage } from 'componentsV2/product/details/content/components/ask-ai/store';
import { useProductDetails } from 'componentsV2/product/details/hooks';
import { useSalesKitStore } from 'componentsV2/product/details/store';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import {
  disableSelectProductTooltip,
  InvalidConfiguration
} from 'pages/products/components';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import {
  GridPricing,
  Product,
  ProductColumnsIds,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';
import { Tabs } from 'componentsV2/product/details/content/components/tabs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { getProductsPrices } from 'pages/products/api';
import { IntelligenceIcon } from '@setvi/shared/components/sicons';
import { ProductAskAI } from 'componentsV2/product/details/content/components';
import { useProductAskAi } from 'componentsV2/product/details/content/components/ask-ai/hook/use-ask-ai';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useSwipeable } from 'react-swipeable';
import {
  MobileAssociatedProducts,
  MobileAssociatedResources,
  MobileOrderHistory,
  MobileProductInfo,
  MobileSalesEnablement,
  MobileSimilarProducts,
  MobileWarehouse
} from './subpages';
import { BackButton, ImagesNavigation, ProductInformation } from './components';
import {
  AskAIButton,
  HiddenScroll,
  imageStyle,
  ImageWrapper,
  InfoContainer,
  PageContainer,
  TabContentContainer
} from './styled';
import { MobileProductCardProps } from '../list/components';
import { TabContentWrapper } from './subpages/styled';
import {
  MobileFloatMenu,
  StyledMenuItem
} from '../float-menu/components/menu-item/styled';
import { MobileProductEnhancer } from '../product-enhancer';
import { mobileDriverStyle } from '../..';
import { DrawerHeaderTitle } from '../drawer-content/components';

export interface MobileProductDetailsProps extends Omit<
  DetailsDialogProps,
  'handleCloseActionBar'
> {
  productCardProps?: Omit<MobileProductCardProps, 'product' | 'checked'>;
  pricingList?: GridPricing[];
  disableSelect?: boolean;
  onPromotionClick?: (product: Product) => void;
  handleProductSelect?: MobileProductCardProps['handleCheck'];
  updateProduct?: (product: Product, newValues: Partial<Product>) => void;
}

const tabStyle = {
  display: 'flex',
  overflow: 'hidden',
  padding: 20,
  paddingTop: 8,
  flex: 1
};

export const MobileProductDetails = ({
  selectedProduct,
  selectedProducts,
  selectedCustomerId,
  defaultOpenedTab,
  orderHistoryValues,
  pricingList = [],
  userType,
  productCardProps,
  configOverride,
  selectionType,
  disableSelect,
  readOnly,
  onClose,
  openProductDetails,
  updateProduct,
  onPromotionClick
}: MobileProductDetailsProps) => {
  const [activeTab, setActiveTab] = useState<ProductDetailsTabsIds>(null);
  const [openGallery, setOpenGallery] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [disableTooltipTitle, setDisableTooltipTitle] = useState('');

  const { configuration: baseConfiguration, invalidConfiguration } =
    useCatalogConfig();
  const configuration = configOverride || baseConfiguration;

  const hideManufacturer =
    !!configOverride &&
    !configOverride?.product?.columns?.some(
      c => c.id === ProductColumnsIds.Manufacturer
    );
  const { product, customer, isLoading, updateDialogProduct } =
    useProductDetails({
      selectedProduct,
      selectedCustomerId,
      userType
    });
  const { resetSalesKitStore } = useSalesKitStore();
  const { reset, openAskAi, setValue, setOpenAskAi } = useAskAIStorage();
  const { enqueueSnackbar } = useSnackbar();
  const { mutationFn } = getProductsPrices();
  const mutation = useMutation(mutationFn);
  const flags = useFlags();
  const {
    questions,
    conversation,
    initLoading,
    loading,
    onSubmit,
    setConversation
  } = useProductAskAi({
    product
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isSwipingRef = useRef(false);

  const tabs = configuration.productDetails?.tabs || [];
  const isSelected = selectedProducts.some(i => i._id === product?._id);
  const productImages = product?.images;

  const handlers = useSwipeable({
    onSwiping: () => {
      isSwipingRef.current = true;
    },
    onSwipedLeft: () => {
      if (productImages?.length > 1) {
        setActiveImage(prev =>
          prev < productImages.length - 1 ? prev + 1 : prev
        );
      }
    },
    onSwipedRight: () => {
      if (productImages?.length > 1) {
        setActiveImage(prev => (prev > 0 ? prev - 1 : prev));
      }
    },
    trackMouse: true,
    delta: 10
  });

  const handleGetPrice = async () => {
    try {
      const response = await mutation.mutateAsync({
        body: {
          accountNumber: customer?.accountNumber,
          products: [{ sku: product.sku, quantity: 1 }]
        }
      });
      const { sell, cost } = response?.pricing?.[0];
      const newValues = {
        price: sell,
        ...(Number(cost) ? { cost } : {})
      };
      updateProduct?.(product, newValues);
      updateDialogProduct(newValues);
    } catch (error) {
      enqueueSnackbar('Error occured while fetching product price', {
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  };

  useEffect(() => {
    setActiveTab(defaultOpenedTab || ProductDetailsTabsIds.PRODUCT_INFO);
  }, [defaultOpenedTab]);

  useEffect(() => {
    if (product?._id) {
      setActiveImage(0);
      scrollContainerRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }
  }, [product?._id]);

  if (!selectedProduct) return null;

  if (isLoading) return <PageLoader />;

  if (invalidConfiguration) return <InvalidConfiguration />;

  return (
    <>
      <SGallery
        fullScreen
        images={product?.images?.map(i => i.path) || []}
        errorImage="/images/product-placeholder.svg"
        defaultOpenedImage={null}
        opened={openGallery}
        onClose={() => setOpenGallery(false)}
      />

      <PageContainer overflow="hidden">
        <HiddenScroll innerRef={scrollContainerRef}>
          <ImageWrapper>
            <BackButton
              onClick={() => {
                onClose();
                resetSalesKitStore();
                reset();
              }}
            />
            <div
              {...handlers}
              onClick={() => {
                if (isSwipingRef.current) {
                  isSwipingRef.current = false;
                  return;
                }
                if (product?.images?.length) setOpenGallery(true);
              }}
              style={{ width: '100%', height: '100%', cursor: 'grab' }}>
              <SETVIImage
                alt={product.name}
                src={
                  productImages?.[activeImage]?.path ||
                  '/images/product-placeholder.svg'
                }
                errorImage="/images/product-placeholder.svg"
                style={{
                  ...imageStyle,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
              />
            </div>
          </ImageWrapper>
          <InfoContainer>
            {productImages.length > 1 && (
              <Box my={2} display="flex" justifyContent="center">
                <ImagesNavigation
                  numberOfItems={productImages.length}
                  activeItem={activeImage}
                  onChange={setActiveImage}
                />
              </Box>
            )}
            <ProductInformation
              product={product}
              customer={customer}
              priceType={configuration.price}
              gridConfig={configuration?.product?.grid}
              pricingList={pricingList}
              productPricesLoading={false}
              hideManufacturer={hideManufacturer}
              onStockClick={() => setActiveTab(ProductDetailsTabsIds.WAREHOUSE)}
              onPromotionClick={onPromotionClick}
              onGetProductPrice={handleGetPrice}
            />
            <Tabs
              containerStyle={{ overflowY: 'auto', padding: '0 20px' }}
              currentTab={activeTab}
              tabs={tabs}
              onTabChange={setActiveTab}
              orderHistoryValues={!!orderHistoryValues}
            />
            {activeTab === ProductDetailsTabsIds.PRODUCT_INFO && (
              <Box style={{ ...tabStyle, paddingBottom: 70 }}>
                <MobileProductInfo product={product} />
                {productCardProps?.handleCheck &&
                  !(selectionType === 'radio' && isSelected) && (
                    <MobileFloatMenu style={{ bottom: 10 }}>
                      <Tooltip
                        arrow
                        open={!!disableTooltipTitle}
                        title={disableTooltipTitle}>
                        <StyledMenuItem
                          style={{
                            flexDirection: 'row',
                            padding: '12px',
                            marginTop: 0
                          }}
                          onClick={() => {
                            if (disableSelect) {
                              setDisableTooltipTitle(
                                disableSelectProductTooltip
                              );
                              setTimeout(() => {
                                setDisableTooltipTitle('');
                              }, 2000);
                              return;
                            }
                            productCardProps.handleCheck(!isSelected, [
                              product
                            ]);
                          }}>
                          {isSelected ? (
                            <CloseIcon style={{ fontSize: 18, fill: '#FFF' }} />
                          ) : (
                            <AddIcon style={{ fontSize: 18, fill: '#FFF' }} />
                          )}
                          <SText
                            fontColor="#fff"
                            title={
                              isSelected
                                ? 'Remove from selection'
                                : 'Add to selection'
                            }
                            weight="semibold"
                          />
                        </StyledMenuItem>
                      </Tooltip>
                    </MobileFloatMenu>
                  )}
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.WAREHOUSE && (
              <Box style={tabStyle}>
                <MobileWarehouse
                  product={product}
                  selectedCustomer={customer}
                  configColumns={
                    configuration.productDetails.tabs.find(
                      i => i.id === ProductDetailsTabsIds.WAREHOUSE
                    )?.columns
                  }
                />
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_RESOURCE && (
              <Box style={{ padding: 20, ...tabStyle }}>
                <MobileAssociatedResources product={product} />
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_PRODUCTS && (
              <Box style={tabStyle}>
                <TabContentContainer display="flex">
                  <MobileAssociatedProducts
                    product={product}
                    selectedProducts={selectedProducts}
                    productCardProps={{
                      ...productCardProps,
                      ...(readOnly && { handleCheck: undefined }),
                      onPromotionClick,
                      onProductClick: i => {
                        openProductDetails(i);
                        setActiveTab(ProductDetailsTabsIds.PRODUCT_INFO);
                      }
                    }}
                  />
                </TabContentContainer>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.SIMILAR_PRODUCTS && (
              <Box style={tabStyle}>
                <TabContentWrapper>
                  <MobileSimilarProducts
                    product={product}
                    customer={customer}
                    selectedProducts={selectedProducts}
                    columns={configuration?.product?.columns}
                    onPromotionClick={onPromotionClick}
                    onProductClick={p => {
                      openProductDetails(p);
                      setActiveTab(ProductDetailsTabsIds.PRODUCT_INFO);
                    }}
                    onStockClick={p => {
                      openProductDetails(p);
                      setActiveTab(ProductDetailsTabsIds.WAREHOUSE);
                    }}
                    {...productCardProps}
                    {...(readOnly && { handleCheck: undefined })}
                  />
                </TabContentWrapper>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.SALES_ENABLEMENT &&
              flags?.salesEnablement && (
                <Box style={tabStyle}>
                  <MobileSalesEnablement product={product} />
                </Box>
              )}
            {activeTab === ProductDetailsTabsIds.ORDER_HISTORY && (
              <Box style={tabStyle}>
                <TabContentContainer>
                  <MobileOrderHistory
                    productId={product._id}
                    selectedProducts={selectedProducts}
                    {...orderHistoryValues}
                  />
                </TabContentContainer>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.PRODUCT_ENHANCER &&
              flags?.productEnhancer && (
                <Box style={tabStyle}>
                  <MobileProductEnhancer product={product} />
                </Box>
              )}
          </InfoContainer>
        </HiddenScroll>
        {flags?.productAssistant && (
          <AskAIButton>
            <SButton
              variant="outlined"
              onClick={() => setOpenAskAi(true)}
              style={{ padding: 0, width: 44, height: 44 }}>
              <IntelligenceIcon style={{ fontSize: 26 }} />
            </SButton>
          </AskAIButton>
        )}
        <Drawer
          anchor="bottom"
          open={openAskAi}
          onClose={() => setOpenAskAi(false)}
          PaperProps={{
            style: {
              ...mobileDriverStyle,
              padding: 20,
              height: '97%'
            }
          }}>
          <>
            <Box mb="12px">
              <DrawerHeaderTitle
                title="Ask AI"
                handleClose={() => setOpenAskAi(false)}
              />
            </Box>
            <ProductAskAI
              onClose={() => {
                setValue('');
                setOpenAskAi(false);
              }}
              onSubmit={onSubmit}
              hideHeader
              conversation={conversation}
              loading={loading}
              product={product}
              initLoading={initLoading}
              questions={questions}
              setConversation={setConversation}
            />
          </>
        </Drawer>
      </PageContainer>
    </>
  );
};
